import { ObjectHash } from '../../types/shared'
import { IndicatorType, OverlayType } from '../constants/common'
import Quote from '../models/quote'

type OptionValuesType = ObjectHash<unknown>
type CalculatedValuesType = ObjectHash<number[]>
interface MainProps<T> {
  quote?: Quote
  options: T
}

export type OnValueCalculatedReturnValue = boolean | undefined
export type OnValueCalculated<T> = ((value: T, dataIndex: number) => OnValueCalculatedReturnValue) | undefined

export class MainCalculation<
  TOptionValues extends OptionValuesType | undefined = undefined,
  TCalculatedValues extends CalculatedValuesType = CalculatedValuesType,
> {
  static type: IndicatorType | OverlayType = IndicatorType.Indicator

  quote: NonNullable<MainProps<TOptionValues>['quote']>
  options: MainProps<TOptionValues>['options']

  protected _calculatedValues: TCalculatedValues

  constructor({ quote, options }: MainProps<TOptionValues>) {
    this.quote = quote ?? new Quote()
    this.options = options
    this._calculatedValues = this.getDefaultCalculatedValues()
  }

  get calculatedValues() {
    return this._calculatedValues
  }

  getDefaultCalculatedValues(): TCalculatedValues {
    throw Error('Implement getDefaultCalculatedValues')
  }

  calculate(_: OnValueCalculated<TCalculatedValues>) {
    throw Error('Implement calculate')
  }

  getAllCalculatedValues(lastNItems: number) {
    const calculatedValues = {} as TCalculatedValues
    Object.keys(this.calculatedValues).forEach((key) => {
      calculatedValues[key as keyof TCalculatedValues] = this.getCalculatedValues(key, lastNItems)
    })
    return calculatedValues
  }

  getCalculatedValues(key: keyof TCalculatedValues, lastNItems: number) {
    return (this.calculatedValues[key] as number[]).slice(-lastNItems) as TCalculatedValues[keyof TCalculatedValues]
  }

  /**
   * This function is used to construct sql script for db table creation
   * and also to build precalculated data object used on node server.
   *
   * Precalculated data object uses database column names as property keys to simplify data mapping on BE
   */
  getDbColumnName() {
    const { type } = this.constructor as typeof MainCalculation
    const typeLabel = type.split('/')[1]
    const optionKeys = Object.keys(this.options ?? {}).sort((key1, key2) => key1.localeCompare(key2))

    const optionsStrings = optionKeys.map((key) => `${key.slice(0, 1)}${this.options![key]}`)
    return [typeLabel, ...optionsStrings].join('_')
  }
}
